import { useMutation } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { mutationFnPOST } from "api/tanStackQuery/helpers";
import { ResponseWithVisibility } from "features/CenterContent/RoleContent/InternalMessage/utils/queries";
import { useInfrastructureEvent } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/infrastructureEventModal/hooks/useInfrastructureEvent";
import { InfrastructureResponse } from "features/CenterContent/RoleContent/TrainMap/StretchBuilder/infrastructureEvents/types/responseSchema";
import { EventInfoMessage } from "features/CenterContent/VehicleDetails/shared/EventInfoMessage";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Semantics } from "shared/components/drops/DropsBadge";
import { FailureMessage } from "shared/components/feedback/FailureMessage/FailureMessage";
import {
  DialogBody,
  DialogRoot,
  DropsDialogContent,
  DropsDialogHeader,
  Flex,
  Skeleton,
} from "shared/components/Spor";
import { isPlannedInfraStructureEvent } from "shared/utils/infrastructureEvent";
import { useStretchBuilder } from "stores/useStretchBuilder";

type EventActionProps = {
  uuids: string[];
};

const eventsByPriority = (
  a: InfrastructureResponse,
  b: InfrastructureResponse,
) => {
  if (isPlannedInfraStructureEvent(a.type)) return 1;
  if (isPlannedInfraStructureEvent(b.type)) return -1;

  const priorityDiff = a.priority - b.priority;
  if (priorityDiff !== 0) return priorityDiff;

  // Check if both have updatedAt before comparing
  if (b.updatedAt && a.updatedAt) {
    return b.updatedAt.compare(a.updatedAt);
  }

  // Fallback: items with updatedAt come first
  if (b.updatedAt) return 1;
  if (a.updatedAt) return -1;

  return 0;
};

export const getInfrastructureEventSemantics = (
  eventType: string,
): Semantics => {
  switch (eventType) {
    case "INFRASTRUCTURE_STRETCH_CLOSED":
    case "INFRASTRUCTURE_GENERAL_WARNING":
      return "alarm";
    case "INFRASTRUCTURE_STRETCH_PARTIALLY_CLOSED":
      return "secondaryAlarm";
    case "INFRASTRUCTURE_SLOW_DRIVING":
      return "warning";
    case "INFRASTRUCTURE_GENERAL_INFO":
    case "INFRASTRUCTURE_PLANNED_MAINTENANCE":
    default:
      return "info";
  }
};

export const InfrastructureEventAction: FC<EventActionProps> = ({ uuids }) => {
  const { data, status } = useInfrastructureEvent(uuids);
  const { t } = useTranslation();

  const [askConfirmationForUuid, setAskConfirmationForUuid] = useState<
    string | null
  >(null);
  const closeAndResetStretchBuilder = useStretchBuilder(
    (state) => state.closeAndResetStretchBuilder,
  );

  const { mutateAsync: closeInfrastructureEvent, status: closeStatus } =
    useMutation({
      mutationFn: (uuid: string) =>
        mutationFnPOST<ResponseWithVisibility, undefined>(
          `${getBackendUrl()}/infrastructure-event/${uuid}/close`,
        ),
    });
  // Close the selectEvent modal after one second if closeEvent is successful
  useEffect(() => {
    if (closeStatus === "success") {
      setTimeout(closeAndResetStretchBuilder, 1000);
    }
  }, [closeStatus]);

  const setStretchBuilderModalState = useStretchBuilder(
    (state) => state.setStretchBuilderModalState,
  );

  const sortedInfrastructureEvents = data?.sort(eventsByPriority) ?? [];

  return (
    <DialogRoot
      open
      onOpenChange={closeAndResetStretchBuilder}
      size="lg"
      placement="center"
    >
      <DropsDialogContent alignItems="center">
        <DropsDialogHeader>
          {t("Aktive infrastrukturhendelser")}
        </DropsDialogHeader>

        <DialogBody display="grid" gap="12px" alignSelf="stretch">
          {status === "pending" && <Skeleton height="36px" />}
          {status === "error" && <FailureMessage />}
          {status === "success" && (
            <ul>
              <Flex flexDir="column" gap="12px">
                {sortedInfrastructureEvents.map((event) => (
                  <EventInfoMessage
                    key={event.uuid}
                    title={event.stretchName}
                    subTitle={event.title ?? t("Generell informasjon")}
                    semantics={getInfrastructureEventSemantics(event.type)}
                    isPlannedMaintenance={isPlannedInfraStructureEvent(
                      event.type,
                    )}
                    versions={[
                      {
                        // Without updatedAt because it uses too much space
                        message:
                          event.message ??
                          t("Kunne ikke finne distribusjonstekst"),
                        internalMessages: event.internalMessages,
                      },
                    ]}
                    showActionButtons
                    askConfirmation={askConfirmationForUuid === event.uuid}
                    setAskConfirmation={(value) =>
                      setAskConfirmationForUuid(value ? event.uuid : null)
                    }
                    setModalOpen={() =>
                      setStretchBuilderModalState({
                        type: "infrastructure",
                        eventInfo: event,
                        stretchBuilderMode: false,
                      })
                    }
                    onClose={() => closeInfrastructureEvent(event.uuid)}
                    initialOpenState
                    infrastructureEventUuid={event.uuid}
                  />
                ))}
              </Flex>
            </ul>
          )}
        </DialogBody>
      </DropsDialogContent>
    </DialogRoot>
  );
};
